import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import CoreServices from '../components/services/CoreServices';
import IndustrySolutions from '../components/services/IndustrySolutions';
import TechStack from '../components/services/TechStack';
import AdditionalServices from '../components/services/AdditionalServices';
import SafeIcon from '../common/SafeIcon';

const featureCards = [
  { t: 'Experienced Team', d: 'Expert developers & designers.', icon: 'construction', imgSrc: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783864509/4538ABDC-D0DC-41D2-A2D4-AC50EE27B4C3_eh09sf.webp' },
  { t: 'Secure Cloud', d: 'Military-grade infrastructure.', icon: 'verified_user', imgSrc: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783864525/4AACC5A6-303F-49BB-BB1F-97C11E28B31A_vvudtm.webp' },
  { t: 'Fast Delivery', d: 'Optimized project timelines.', icon: 'speed', imgSrc: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783864525/4AACC5A6-303F-49BB-BB1F-97C11E28B31A_vvudtm.webp' },
  { t: 'Mobile-First', d: 'Perfect on every device.', icon: 'smartphone', imgSrc: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783864573/BEBE7EA2-7910-48C6-906D-8F5915992704_lb2coe.webp' },
  { t: 'SEO-Friendly', d: 'Built to rank on Google.', icon: 'search', imgSrc: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783864607/BF7E074F-6A92-4CCA-8DFE-DF1BCFA25F20_dpa7tk.webp' },
  { t: 'Transparent Pricing', d: 'No hidden fees, ever.', icon: 'attach_money', imgSrc: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783864632/ECB28D45-D4A2-44C2-85DD-F7B3901A683C_k51exr.webp' },
  { t: 'Dedicated Support', d: '24/7 technical assistance.', icon: 'support_agent', imgSrc: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783864649/84889DD1-FB21-4D1E-ADB2-DC09D54ED28F_j9jtk5.webp' },
  { t: 'Scalable Solutions', d: 'Growth-ready architecture.', icon: 'trending_up', imgSrc: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783864373/A01C9E27-1782-494D-8654-6AD493D2512E_pvgi4l.webp' },
  { t: 'HostFig Studio', d: 'Professional catalog imagery.', icon: 'camera_alt', imgSrc: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783864694/EDA8DDAA-BD1F-4F8B-8F62-BD7CDE2A2A40_wwjhtw.webp' },
  { t: 'Modern Design', d: 'Visuals that captivate.', icon: 'brush', imgSrc: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783864719/27AFE94C-7B03-4F18-8844-C9C7D2BA31B0_nc7vzq.webp' }
];

const InteractiveCard = ({ title, desc, iconName, imgSrc }) => {
  const cardRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const updateScale = () => {
      if (cardRef.current) {
        const { clientWidth, clientHeight } = cardRef.current;
        setScale(Math.min(clientWidth / 660, clientHeight / 836));
      }
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  return (
    <div ref={cardRef} className="card-frame w-full" style={{ aspectRatio: '319/404', borderRadius: 'clamp(18px, 1.7vw, 28px)', overflow: 'hidden', background: '#0e0d0c', boxShadow: '0 36px 70px -34px rgba(28, 18, 8, .42), 0 4px 14px -8px rgba(0, 0, 0, .18)' }}>
      <div className="direct-card w-full h-full relative overflow-hidden bg-[#1a1714]" style={{ isolation: 'isolate' }}>
        <div className="direct-card__artboard absolute top-1/2 left-1/2 origin-center bg-[#1a1714]" style={{ width: 660, height: 836, borderRadius: 30, overflow: 'hidden', isolation: 'isolate', containerType: 'inline-size', transform: `translate(-50%, -50%) scale(${scale})` }}>
          {/* Background image only, video removed as requested */}
          <img src={imgSrc} alt={title} className="direct-card__photo absolute inset-0 w-full h-full object-cover z-0" style={{ objectPosition: '50% 42%' }} />
          
          <div className="direct2-grade absolute inset-0 z-10 pointer-events-none" style={{ background: 'linear-gradient(to top, rgba(8, 6, 4, .92) 0%, rgba(8, 6, 4, .6) 13%, rgba(8, 6, 4, .1) 28%, rgba(8, 6, 4, 0) 42%), linear-gradient(to bottom, rgba(20, 14, 8, .34) 0%, rgba(20, 14, 8, 0) 20%)' }} />
          <div className="direct-footer absolute z-40 left-[6.2%] right-[6.2%] bottom-[5.4%] text-white">
            <div className="direct-footer__head flex items-center gap-[3%] mb-[3.2%]">
              <div className="direct-footer__icon flex-none w-[9.6%] aspect-square rounded-full flex items-center justify-center bg-blue-600 text-white" style={{ boxShadow: '0 10px 24px -10px rgba(37, 99, 235, .6)' }}>
                <span className="material-icons" style={{ fontSize: 'clamp(15px, 5.6cqw, 34px)', lineHeight: 1 }}>{iconName}</span>
              </div>
              <div className="direct-footer__title font-bold whitespace-nowrap" style={{ lineHeight: 1.05, letterSpacing: '-0.6px', fontSize: 'clamp(18px, 5.6cqw, 36px)' }}>
                {title}
              </div>
            </div>
            <div className="direct-footer__desc font-medium max-w-full" style={{ color: '#d7d2c9', lineHeight: 1.28, fontSize: 'clamp(13px, 4.2cqw, 27px)' }}>
              {desc}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ServicesPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    const iconsId = 'material-icons-link';
    if (!document.getElementById(iconsId)) {
      const link = document.createElement('link');
      link.id = iconsId;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
      document.head.appendChild(link);
    }
  }, []);

  return (
    <div className="pt-24 pb-0 w-full bg-white font-sans overflow-hidden">
      {/* Hero Section */}
      <section className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center pt-16 pb-12">
        <motion.h1 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-7xl font-bold tracking-tight text-slate-900 mb-8"
        >
          Our <span className="text-blue-600">Services</span>
        </motion.h1>
        <motion.p 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.1 }}
          className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed"
        >
          Comprehensive digital solutions engineered for performance, scale, and results. We provide the complete infrastructure your business needs to thrive online.
        </motion.p>
      </section>

      {/* Core Services Section */}
      <CoreServices />

      {/* Industry Solutions */}
      <IndustrySolutions />

      {/* Why Choose HostFig Feature Cards */}
      <section className="py-32 bg-[#f8fafc]">
        <style>{`
          .cards-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: clamp(12px, 1.2vw, 20px);
            align-items: stretch;
          }
          @media (max-width: 1100px) {
            .cards-grid {
              grid-template-columns: repeat(2, 1fr);
              gap: clamp(16px, 3vw, 24px);
            }
          }
          @media (max-width: 600px) {
            .cards-grid {
              grid-template-columns: 1fr;
              max-width: 380px;
              margin: 0 auto;
            }
          }
        `}</style>
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="text-center mb-16 flex flex-col items-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-6 text-slate-900">
              Why Choose HostFig?
            </h2>
            <p className="text-lg max-w-2xl mx-auto mb-12 text-slate-600">
              We combine technical excellence with a partnership-first approach to build lasting digital foundations.
            </p>
          </div>
          <div className="cards-grid">
            {featureCards.map((item, i) => (
              <InteractiveCard key={i} title={item.t} desc={item.d} iconName={item.icon} imgSrc={item.imgSrc} />
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <TechStack />

      {/* Support & Maintenance Highlights */}
      <section className="py-32 bg-blue-600 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-5xl font-bold mb-8">Support & Maintenance</h2>
              <p className="text-blue-100 text-xl mb-12 leading-relaxed">We don't just build and leave. We partner with you to ensure long-term stability and growth.</p>
              <div className="grid sm:grid-cols-2 gap-6">
                {[
                  'Priority Tech Support', 'Performance Monitoring', 'Security Monitoring', 'Monthly Maintenance',
                  'Content Updates', 'Feature Enhancements', 'Emergency Assistance', 'Website Health Checks'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center">
                      <SafeIcon icon="Check" className="w-3 h-3 text-white" />
                    </div>
                    <span className="font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-3xl p-10 border border-white/20 text-left">
              <h3 className="text-2xl font-bold mb-6">Need a custom plan?</h3>
              <p className="text-blue-100 mb-8">Every business has unique requirements. Contact our team to design a support package that fits your specific needs.</p>
              <button 
                onClick={() => navigate('/contact')}
                className="w-full bg-white text-blue-600 font-bold py-4 rounded-xl hover:bg-blue-50 transition-colors"
              >
                Contact Support Team
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Professional Services */}
      <AdditionalServices />
    </div>
  );
};

export default ServicesPage;